<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted, watch, nextTick, getCurrentInstance } from "vue";
  import { storeToRefs } from "pinia";
  import { useIntervalFn } from "@vueuse/core";
  import { Plus } from "lucide-vue-next";
  import { useGridRowsStore, type GridColumnOption } from "@stores/survey/grids/gridRows";
  import { useCurrentInterviewStore } from "@stores/survey/grids/currentInterview";
  import { useGridsStore } from "@stores/survey/grids/grids";
  import { useSurveyStore } from "@stores/survey/survey";
  import { fetchInterviewByToken } from "@/utils/fetchInterviewByToken";
  import { useGridFilters } from "@/utils/composables/filters/useGridFilters";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Button } from "@/components/ui/button";
  import CreditLimitBanner from "@/components/Survey/CreditLimitBanner.vue";
  import GridAddColumnMenu from "@/components/Survey/Grids/GridAddColumnMenu.vue";
  import GridAddRowsDialog from "@/components/Survey/Grids/GridAddRowsDialog.vue";
  import GridAskQuestionDialog from "@/components/Survey/Grids/GridAskQuestionDialog.vue";
  import GridEmptyState from "@/components/Survey/Grids/GridEmptyState.vue";
  import GridPopulationAnswerSheet from "@/components/Survey/Grids/GridPopulationAnswerSheet.vue";
  import GridPopulationDrawer from "@/components/Survey/Grids/GridPopulationDrawer.vue";
  import GridPopulationTable from "@/components/Survey/Grids/GridPopulationTable.vue";
  import GridRecodeDialog from "@/components/Survey/Grids/GridRecodeDialog.vue";
  import GridSelector from "@/components/Survey/Grids/GridSelector.vue";
  import GridTable from "@/components/Survey/Grids/GridTable.vue";
  import InterviewDetailPanel from "@/components/Survey/Grids/InterviewDetailPanel.vue";
  import type { GridTableColumn } from "@/components/Survey/Grids/gridColumns";
  import type { GridCell, GridColumn, GridRow, GridSummary, Interview } from "@/types";

  interface Cable {
    subscribe: (params: Record<string, unknown>) => void;
    perform: (params: Record<string, unknown>) => void;
    _addChannel: (name: string, handlers: Record<string, unknown>, owner: Record<string, unknown>) => void;
    _removeChannel: (name: string, uid: number) => void;
  }

  interface SelectedAnswer {
    prompt: string;
    cell?: GridCell;
    population?: { name?: string; color?: string | null; respondents_count?: number; outdated?: boolean };
  }

  const EXCLUDED_KEYS = [
    "id",
    "token",
    "language",
    "favorite",
    "summary",
    "highlights",
    "conversation",
    "profile_tags",
    "key_highlights",
    "noteworthy_quotes",
    "personal_notes",
    "quick_take",
  ];

  const gridRowsStore = useGridRowsStore();
  const { rows, objectives, columnOptions, searchQuery, currentPage, perPage, totalCount, loading } =
    storeToRefs(gridRowsStore);
  const { selectRow, toggleFavorite, fetchColumnOptions, fetch: fetchRows, goToPage, ensureColumnForQuestion } =
    gridRowsStore;

  const gridsStore = useGridsStore();
  const { currentGrid, populations } = storeToRefs(gridsStore);
  const {
    fetch: fetchGrids,
    fetchPopulations,
    addColumn,
    removeColumn,
    removeRow,
    reorder,
    materialize,
    hasColumn,
    cellFor,
    applyCellUpdate,
    refreshIfStale,
    refreshRow,
    refreshSummary,
  } = gridsStore;

  const { filterConfigs, activeFiltersCount, handleFilterToggle, handleClear, loadFiltersFromStorage } = useGridFilters();
  const { clearCurrentInterview } = useCurrentInterviewStore();
  const surveyStore = useSurveyStore();

  useIntervalFn(refreshIfStale, 10_000);

  const isPopulationGrid = computed(() => currentGrid.value?.grid_type === "population");
  const canAddRows = computed(() => isPopulationGrid.value || !currentGrid.value?.default_grid);
  const isEmptyGrid = computed(
    () => !!currentGrid.value && !currentGrid.value.default_grid && !currentGrid.value.rows.length,
  );
  const subtitle = computed(() => {
    if (isPopulationGrid.value) return `${currentGrid.value?.rows.length ?? 0} populations`;
    return totalCount.value ? `${totalCount.value} respondents` : undefined;
  });

  const addRowsOpen = ref(false);
  const askQuestionOpen = ref(false);
  const populationDrawerOpen = ref(false);
  const populationDrawerToken = ref<string | null>(null);
  const selectedAnswer = ref<SelectedAnswer | null>(null);
  const selectedPopulationAnswer = ref<{ prompt: string; cell?: GridCell; summary?: GridSummary } | null>(null);
  const selectedInterview = ref<Interview | null>(null);
  const scrollToMessageId = ref<string | number | null>(null);
  const detailPanelRef = ref<{ activeTab: string } | null>(null);
  const recodeDialogRef = ref<{ openNew: () => void; openEdit: (cardToken: string) => void } | null>(null);

  const isPanelOpen = computed({
    get: () => !!selectedInterview.value,
    set: (value: boolean) => {
      if (!value) selectedInterview.value = null;
    },
  });

  const selectedIndex = computed(() => rows.value.findIndex((row) => row.id === selectedInterview.value?.id));
  const hasPrevious = computed(() => selectedIndex.value > 0);
  const hasNext = computed(() => selectedIndex.value >= 0 && selectedIndex.value < rows.value.length - 1);

  const matchesColumn = (option: GridColumnOption, column: GridColumn) => option.id === column.question_id;

  const tableColumns = computed<GridTableColumn[]>(() =>
    (currentGrid.value?.columns ?? [])
      .map((column) => ({
        ...column,
        option: column.kind === "card" ? columnOptions.value.find((option) => matchesColumn(option, column)) : undefined,
      }))
      .filter(
        (column) =>
          column.kind === "ai_question" || (column.option && !EXCLUDED_KEYS.includes(column.option.question_name)),
      ),
  );

  const profileColumns = computed(() => tableColumns.value.flatMap((column) => (column.option ? [column.option] : [])));

  const availableColumns = computed(() =>
    columnOptions.value.filter((option) => !EXCLUDED_KEYS.includes(option.question_name) && !hasColumn(option.id)),
  );

  const openTab = (tab: string) =>
    setTimeout(() => {
      if (detailPanelRef.value) detailPanelRef.value.activeTab = tab;
    }, 0);

  const showInterview = (interview: Interview) => {
    selectedInterview.value = interview;
    selectRow(interview);
  };

  const handleSelectInterview = (interview: Interview) => {
    selectedAnswer.value = null;
    showInterview(interview);
  };

  const selectSibling = (offset: number) => {
    const sibling = rows.value[selectedIndex.value + offset];
    if (sibling) handleSelectInterview(sibling);
  };

  const handleSelectAnswer = (interview: Interview, column: GridTableColumn) => {
    selectedAnswer.value = { prompt: column.prompt ?? "", cell: cellFor(column.id, interview.id) ?? undefined };
    showInterview(interview);
    openTab("answer");
  };

  const showRespondent = async (respondentToken: string, messageId: string | number | null = null) => {
    const found = await fetchInterviewByToken(
      String(surveyStore.token),
      respondentToken,
      typeof messageId === "number" ? messageId : null,
    );
    if (found) selectedInterview.value = found.interview;
    return found;
  };

  const handleSelectPopulationCell = async (row: GridRow, column: GridColumn) => {
    const cell = column.cells.find((item) => item.search_group_id === row.search_group_id);
    const respondentToken = cell?.citations?.[0]?.respondent_id;
    if (!respondentToken) {
      selectedPopulationAnswer.value = { prompt: column.prompt ?? "", cell };
      return;
    }

    const population = populations.value.find((item) => item.id === row.search_group_id);
    selectedAnswer.value = {
      prompt: column.prompt ?? "",
      cell,
      population: {
        name: population?.name,
        color: population?.color,
        respondents_count: row.respondents_count,
        outdated: row.outdated,
      },
    };
    if (await showRespondent(respondentToken)) openTab("answer");
  };

  const handleSelectSummary = (column: GridColumn) => {
    const summary = (currentGrid.value?.summaries ?? []).find((item) => item.grid_column_id === column.id);
    selectedPopulationAnswer.value = { prompt: column.prompt ?? "", summary };
  };

  const handleOpenCitation = async (messageId: string | number) => {
    const citation = selectedAnswer.value?.cell?.citations?.find((item) => item.message_id === messageId);
    if (citation?.respondent_id && citation.respondent_id !== selectedInterview.value?.token) {
      await showRespondent(citation.respondent_id, messageId);
    }
    await nextTick();
    if (!detailPanelRef.value) return;

    detailPanelRef.value.activeTab = "conversation";
    scrollToMessageId.value = messageId;
  };

  const handleSelectConversation = (interview: Interview, messageId: string | number) => {
    showInterview(interview);
    scrollToMessageId.value = null;
    setTimeout(() => {
      if (!detailPanelRef.value) return;
      detailPanelRef.value.activeTab = "conversation";
      scrollToMessageId.value = messageId;
    }, 0);
  };

  const handleToggleFavorite = async (id: number) => {
    await toggleFavorite(id);
    if (selectedInterview.value?.id !== id) return;
    selectedInterview.value =
      rows.value.find((row) => row.id === id) ?? { ...selectedInterview.value, favorite: !selectedInterview.value.favorite };
  };

  const handleRefreshMedia = async () => {
    const id = selectedInterview.value?.id;
    if (!id) return;
    await fetchRows();
    const fresh = rows.value.find((row) => row.id === id);
    if (fresh) showInterview(fresh);
  };

  const handleEditRecode = (column: GridTableColumn) => {
    if (column.option?.card_token) recodeDialogRef.value?.openEdit(column.option.card_token);
  };

  const toggleColumn = async (option: GridColumnOption) => {
    const inGrid = (currentGrid.value?.columns ?? []).find((column) => matchesColumn(option, column));
    if (!inGrid) return addColumn(option.id);
    if (!inGrid.id) await materialize();
    const persisted = (currentGrid.value?.columns ?? []).find((column) => matchesColumn(option, column));
    if (persisted?.id) await removeColumn(persisted.id);
  };

  const removeGridColumn = (column: GridTableColumn) =>
    column.kind === "ai_question" ? removeColumn(column.id) : column.option && toggleColumn(column.option);

  const sameColumn = (a: GridColumn, b: GridColumn) =>
    a.id && b.id ? a.id === b.id : a.kind === b.kind && a.question_id === b.question_id;

  const reorderColumns = async (ordered: GridColumn[]) => {
    await materialize();
    const persisted = currentGrid.value?.columns ?? [];
    const positions = ordered.flatMap((column, index) => {
      const match = persisted.find((item) => sameColumn(item, column));
      return match ? [{ id: match.id, position: index + 1 }] : [];
    });
    await reorder("columns", positions);
  };

  const gridRowOf = (interview: Interview) =>
    currentGrid.value?.rows.find((row) => row.survey_customer_id === interview.id);

  const reorderRows = async (ordered: Interview[]) => {
    const slots = ordered.flatMap((interview) => gridRowOf(interview)?.position ?? []).sort((a, b) => a - b);
    rows.value = ordered;
    await reorder(
      "rows",
      ordered.flatMap((interview, index) => {
        const row = gridRowOf(interview);
        return row ? [{ id: row.id, position: slots[index] }] : [];
      }),
    );
  };

  const reorderPopulationRows = (ordered: GridRow[]) =>
    reorder(
      "rows",
      ordered.map((row, index) => ({ id: row.id, position: index + 1 })),
    );

  const removeGridRow = async (interview: Interview) => {
    const row = gridRowOf(interview);
    if (!row) return;
    await removeRow(row.id);
    await fetchRows();
  };

  const editPopulation = (token: string | null) => {
    addRowsOpen.value = false;
    populationDrawerToken.value = token;
    populationDrawerOpen.value = true;
  };

  watch(
    () => currentGrid.value?.token,
    (token, previousToken) => {
      if (token && previousToken) fetchRows(1);
    },
  );

  const instance = getCurrentInstance();
  const cable = (instance?.proxy as unknown as { $cable: Cable }).$cable;
  const owner = { _uid: instance?.uid, _value: instance?.proxy } as Record<string, unknown>;

  const handleRecodeComputed = async (message: { questions?: { name: string }[] }) => {
    await fetchColumnOptions();
    await fetchRows();
    const questionName = message.questions?.[0]?.name;
    if (questionName) await ensureColumnForQuestion(questionName);
  };

  onMounted(async () => {
    clearCurrentInterview();
    await surveyStore.fetch(surveyStore.token);

    const surveyToken = String(surveyStore.token);
    cable.subscribe({ channel: "ChartsRecodesChannel", survey_token: surveyToken });
    cable._addChannel(
      "ChartsRecodesChannel",
      {
        connected: () =>
          cable.perform({
            channel: "ChartsRecodesChannel",
            action: "subscribe",
            data: { survey_token: surveyToken },
          }),
        received: handleRecodeComputed,
      },
      owner,
    );
    cable.subscribe({ channel: "GridsChannel", survey_token: surveyToken });
    cable._addChannel("GridsChannel", { received: applyCellUpdate }, owner);

    await fetchGrids();
    await fetchPopulations();
    await fetchColumnOptions();
    if (!(await loadFiltersFromStorage())) await fetchRows();
    if (rows.value.length > 0) selectRow(rows.value[0]);
  });

  onUnmounted(() => {
    if (instance) {
      cable._removeChannel("ChartsRecodesChannel", instance.uid);
      cable._removeChannel("GridsChannel", instance.uid);
    }
    gridsStore.reset();
    gridRowsStore.reset();
  });
</script>

<template>
  <IndexPageLayout
    title="Grids"
    :subtitle="subtitle"
    padding="sm"
    action-label="Ask AI question"
    :action-icon="AiStar"
    @action="askQuestionOpen = true">
    <template #actions>
      <GridSelector />
    </template>

    <template #filters>
      <IndexPageFilters
        v-if="!isPopulationGrid"
        v-model:search-model-value="searchQuery"
        search-placeholder="Search all columns">
        <template #filters>
          <FilterDropdown
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            @toggle="handleFilterToggle"
            @clear="handleClear" />
        </template>
        <template #actions>
          <Button
            v-if="canAddRows"
            variant="outline"
            @click="addRowsOpen = true">
            <Plus class="size-4" />
            Add respondents
          </Button>
          <GridAddColumnMenu
            :columns="availableColumns"
            @add-column="toggleColumn"
            @add-recode="recodeDialogRef?.openNew()"
            @ask-question="askQuestionOpen = true" />
        </template>
      </IndexPageFilters>
      <div
        v-else
        class="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          @click="addRowsOpen = true">
          <Plus class="size-4" />
          Add populations
        </Button>
      </div>
    </template>

    <CreditLimitBanner />
    <GridEmptyState
      v-if="isEmptyGrid"
      :population-grid="isPopulationGrid"
      @add-rows="addRowsOpen = true" />
    <GridPopulationTable
      v-else-if="isPopulationGrid"
      @select-cell="handleSelectPopulationCell"
      @select-summary="handleSelectSummary"
      @reorder-columns="reorderColumns"
      @reorder-rows="reorderPopulationRows"
      @remove-column="(column) => removeColumn(column.id)"
      @remove-row="(row) => removeRow(row.id)"
      @refresh-row="(row) => refreshRow(row.id)"
      @refresh-summary="(column) => refreshSummary(column.id)" />
    <GridTable
      v-else
      :interviews="rows"
      :columns="tableColumns"
      :selected-interview-id="selectedInterview?.id"
      :loading="loading"
      :can-add-rows="canAddRows"
      :current-page="currentPage"
      :total="totalCount"
      :page-size="perPage"
      @select="handleSelectInterview"
      @select-conversation="handleSelectConversation"
      @select-answer="handleSelectAnswer"
      @toggle-favorite="handleToggleFavorite"
      @edit-recode="handleEditRecode"
      @reorder-columns="reorderColumns"
      @reorder-rows="reorderRows"
      @remove-column="removeGridColumn"
      @remove-row="removeGridRow"
      @page-change="goToPage" />

    <InterviewDetailPanel
      ref="detailPanelRef"
      v-model:open="isPanelOpen"
      :interview="selectedInterview ?? undefined"
      :objectives="objectives"
      :columns="profileColumns"
      :answer="selectedAnswer ?? undefined"
      :has-previous="hasPrevious"
      :has-next="hasNext"
      :scroll-to-message-id="scrollToMessageId ?? undefined"
      @refresh-media="handleRefreshMedia"
      @toggle-favorite="handleToggleFavorite"
      @open-citation="handleOpenCitation"
      @previous="selectSibling(-1)"
      @next="selectSibling(1)" />

    <template #dialogs>
      <GridRecodeDialog ref="recodeDialogRef" />
      <GridAddRowsDialog
        v-model:open="addRowsOpen"
        @added="fetchRows(1)"
        @edit-population="editPopulation" />
      <GridPopulationDrawer
        v-model:open="populationDrawerOpen"
        :token="populationDrawerToken" />
      <GridAskQuestionDialog v-model:open="askQuestionOpen" />
      <GridPopulationAnswerSheet v-model:answer="selectedPopulationAnswer" />
    </template>
  </IndexPageLayout>
</template>
